import { selectWarehouseMoreActions, selectWarehouseSelectedGateGateway, selectWarehouseStatePlace, updateMoreActions } from 'containers/Warehouses/warehousesSlice'
import { useReduxDispatch, useReduxSelector } from 'store'
import { useEffect, useRef, useState } from 'react'
import { GATE_STATUS } from 'utils/constants'
import { POST } from 'rest/request'
import { api } from 'rest/urls'

type Type = 'gates' | 'gateways'
type BaseActionType = Exclude<warehouse.OpeningClosingActionType, 'stop'>

const useOpeningClosingActions = (
  placeId: string,
  actions?: warehouse.OpeningClosingActions
) => {
  const [isSending, setSending] = useState(false)
  const [stoppedAction, setStoppedAction] = useState<BaseActionType | ''>('')
  const place = useReduxSelector(state => selectWarehouseStatePlace(state, placeId))
  const selected = useReduxSelector(state => selectWarehouseSelectedGateGateway(state, placeId))
  const moreActions = useReduxSelector(selectWarehouseMoreActions)
  const gateId = selected?.gate || Object.keys(place?.gates || {})[0]
  const gatewayId = selected?.gateway || Object.keys(place?.gateways || {})[0]
  const gatewaysState = place?.gateways?.[gatewayId]?.state
  const gateState = place?.gates?.[gateId]?.state
  const currentGateState = useRef(gateState)
  currentGateState.current = gateState

  const dispatch = useReduxDispatch()
  const action = async <T extends Type>(objectType: T, objectId: string, actionType?: warehouse.OpeningClosingGateGatewayActionType) => {
    if (isSending) return
    try {
      const action = actions?.[objectType]?.[objectId]?.[actionType || 'open']
      if (!action) throw new Error('No action')
      if (action.type === 'request' && action.method === 'POST') {
        setSending(true)
        const body = action.payload
        const response = await POST(`${api}${action.url}`, { body })
        if (response?.result === 'ok') {
          if (
            objectType === 'gates' &&
            actionType !== 'stop' &&
            stoppedAction
          ) {
            setStoppedAction('')

          } else if (
            objectType === 'gates' &&
            actionType === 'stop' &&
            gateState !== GATE_STATUS.stopped
          ) {
            setStoppedAction((gateState === GATE_STATUS.closed || gateState === GATE_STATUS.opening)
              ? 'open'
              : 'close'
            )

          } else if (objectType === 'gates' && actionType === 'open') {
            dispatch(updateMoreActions({ gateId: objectId, value: true }))
          }
        }
        setSending(false)
        return response

      } else {
        throw new Error('No supported action')
      }
    } catch (error) {
      setSending(false)
      console.error(error)
    }
  }

  useEffect(() => {
    if ((gateState === GATE_STATUS.opened || gateState === GATE_STATUS.closed) && stoppedAction) {
      setStoppedAction('')
    }
  }, [gateState, stoppedAction])

  return {
    place,
    isSending,
    gateId,
    gatewayId,
    moreActions: moreActions?.[gateId],
    stoppedAction,
    status: {
      gateway: gatewaysState,
      gate: gateState,
    },
    action
  }
}

export default useOpeningClosingActions